import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { readLocalStorage } from "@/lib/safe-storage";
import NotFound from "@/pages/not-found";
import AdminLayout from "@/components/admin-layout";
import Layout from "@/components/layout";
import FounderLayout from "@/components/founder-layout";
import InvestorLayout from "@/components/investor-layout";
import AdminHome from "@/pages/admin/admin-home";
import AdminTeam from "@/pages/admin/admin-team";
import AdminAgents from "@/pages/admin/admin-agents";
import AdminAgentDetail from "@/pages/admin/admin-agent-detail";
import AdminBankers from "@/pages/admin/admin-bankers";
import AdminAdvisorHealth from "@/pages/admin/admin-advisor-health";
import AdminPrds from "@/pages/admin/admin-prds";
import AdminSpend from "@/pages/admin/admin-spend";
import AdminInvestors from "@/pages/admin/admin-investors";
import AdminViren from "@/pages/admin/admin-viren";
import AdminRateek from "@/pages/admin/admin-rateek";
import AdminSecondary from "@/pages/admin/admin-secondary";
import AdminHealth from "@/pages/admin/admin-health";
import AdminTimestampTrail from "@/pages/admin/admin-timestamp-trail";
import AdminOnboardingDrips from "@/pages/admin/admin-onboarding-drips";
import AdminCampaigns from "@/pages/admin/admin-campaigns";
import AdminEmail from "@/pages/admin/admin-email";
import AdminBulletins from "@/pages/admin/admin-bulletins";
import AdminConversations from "@/pages/admin/admin-conversations";
import AdminCampaignNew from "@/pages/admin/admin-campaign-new";
import AdminCampaignDetail from "@/pages/admin/admin-campaign-detail";
import AdminCampaignsRoadmap from "@/pages/admin/admin-campaigns-roadmap";
import AdminResearchPipeline from "@/pages/admin/admin-research-pipeline";
import AdminResearchPipelineDetail from "@/pages/admin/admin-research-pipeline-detail";
import BankerLayout from "@/components/banker-layout";
import BankerLogin from "@/pages/banker/banker-login";
import BankerChangePassword from "@/pages/banker/banker-change-password";
import BankerDashboard from "@/pages/banker/banker-dashboard";
import BankerTasks from "@/pages/banker/banker-tasks";
import BankerTaskDetail from "@/pages/banker/banker-task-detail";
import BankerMeera from "@/pages/banker/banker-meera";
import BankerInvestors from "@/pages/banker/banker-investors";
import BankerInvestorSignals from "@/pages/banker/banker-investor-signals";
import BankerCompanies from "@/pages/banker/banker-companies";
import BankerMyFounders from "@/pages/banker/banker-my-founders";
import BankerArtifacts from "@/pages/banker/banker-artifacts";
import BankerInbox from "@/pages/banker/banker-inbox";
import BankerMail from "@/pages/banker/banker-mail";
import BankerConversations from "@/pages/banker/banker-conversations";
import BankerCalendar from "@/pages/banker/banker-calendar";
import CommandCenter from "@/pages/command-center";
import Companies from "@/pages/companies";
import CompanyDetail from "@/pages/company-detail";
import Founders from "@/pages/founders";
import Investors from "@/pages/investors";
import Updates from "@/pages/updates";
import Documents from "@/pages/documents";
import MediaPage from "@/pages/media";
import Audit from "@/pages/audit";
import CohortMembers from "@/pages/cohort-members";
import Announcements from "@/pages/announcements";
import ReviewQueue from "@/pages/review-queue";
import TasksPage from "@/pages/tasks-page";
import FounderHome from "@/pages/founder/founder-home";
import FounderCheckin from "@/pages/founder/founder-checkin";
import FounderApprovals from "@/pages/founder/founder-approvals";
import FounderPreview from "@/pages/founder/founder-preview";
import InvestorHome from "@/pages/investor/investor-home";
import InvestorMicrosite from "@/pages/investor/investor-microsite";
import InvestorLogin from "@/pages/investor/investor-login";
import InvestorChangePassword from "@/pages/investor/investor-change-password";
import MasterAdminLogin from "@/pages/investor/master-admin-login";
import SetPasswordPage from "@/pages/auth/set-password";
import FounderLogin1to10 from "@/pages/founder-1to10/login";
import { FounderCohortProvider } from "@/lib/founder-cohort";
import { isKnownSlug, themeForSlug } from "@/config/cohort-themes";

// Rendered when a founder URL has a slug not registered in cohort-themes.ts.
// We intentionally do NOT 404 — the path shape is correct, the slug just
// isn't a real cohort. The clearer message keeps the founder from
// re-trying with typos and gets them to the support contact fast.
function UnknownCohortPage({ slug }: { slug: string }) {
  return (
    <div className={/* ALLOW_HARDCODED_COLOR: dark anchor remapped by [data-founder-theme="light"] */ "min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6"}>
      <div className="max-w-md text-center">
        <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-[#FF5C00]/15 border border-[#FF5C00]/30 flex items-center justify-center text-[#FF5C00] font-bold">!</div>
        <h1 className="font-serif text-2xl font-bold">Unknown cohort</h1>
        <p className="text-sm text-white/70 mt-3">
          We don't recognise the cohort <code className="text-[#FF5C00] font-mono">{slug}</code>. The URL may have a typo, or the cohort is not yet configured.
        </p>
        <p className="text-sm text-white/60 mt-4">
          If you're a founder in a Favcy Select cohort, check the link your advisor sent you, or write to <a className="text-[#FF5C00] hover:underline" href="mailto:pc@favcy.in">pc@favcy.in</a>.
        </p>
      </div>
    </div>
  );
}
import FounderChangePassword1to10 from "@/pages/founder-1to10/change-password";
import FounderCommandCenter1to10 from "@/pages/founder-1to10/command-center";
import FounderStellarOnboarding from "@/pages/founder-1to10/stellar-onboarding";
import FounderOnboardingBegin from "@/pages/founder-1to10/onboarding-begin";
import FounderStubPage from "@/pages/founder-1to10/stub-page";
import FounderAdvisorThread from "@/pages/founder-1to10/thread";
import FounderReviewRoom from "@/pages/founder-1to10/review-room";
import FounderProof from "@/pages/founder-1to10/proof";
import FounderReadiness from "@/pages/founder-1to10/readiness";
import FounderWeeklyUpdate from "@/pages/founder-1to10/updates";
import FounderInvestorPreview from "@/pages/founder-1to10/preview";
import FounderSettings from "@/pages/founder-1to10/settings";
import FounderMissions from "@/pages/founder-1to10/missions";
import FounderNetwork from "@/pages/founder-1to10/network";
import FounderMentors from "@/pages/founder-1to10/mentors";
import FounderMentorDetail from "@/pages/founder-1to10/mentor-detail";
import MentorLayout from "@/components/mentor-layout";
import MentorLogin from "@/pages/mentor/login";
import MentorsApply from "@/pages/mentors-apply";
import MentorActivate from "@/pages/mentor/activate";
import MentorHome from "@/pages/mentor/home";
import MentorMissionDetail from "@/pages/mentor/mission-detail";
import MentorCompanyDetail from "@/pages/mentor/company-detail";
import MentorEngagementDetail from "@/pages/mentor/engagement-detail";
import FounderEngagementDetail from "@/pages/founder-1to10/engagement-detail";
import MentorInterests from "@/pages/mentor/interests";
import MentorProposals from "@/pages/mentor/proposals";
import MentorEngagements from "@/pages/mentor/engagements";
import MentorPublicProfile from "@/pages/mentor-public-profile";
import AdminMentors from "@/pages/admin/admin-mentors";
import FounderAsk from "@/pages/founder-1to10/ask";
import AdminFounderPortal from "@/pages/admin/admin-founder-portal";
import Founder1to10Layout from "@/components/founder-1to10-layout";
import InvestorPortalDashboard from "@/pages/investor/investor-portal-dashboard";
import InvestorPortalCompany from "@/pages/investor/investor-portal-company";
import InvestorPortalRofr from "@/pages/investor/investor-portal-rofr";
import InvestorPortalDocuments from "@/pages/investor/investor-portal-documents";
import InvestorPortalFilesFromBanker from "@/pages/investor/investor-portal-files-from-banker";
import InvestorPortalSettings from "@/pages/investor/investor-portal-settings";
import InvestorPortalInbox from "@/pages/investor/investor-portal-inbox";
import InvestorResearchIndex, {
  InvestorResearchNarrative,
  InvestorResearchReadingRoom,
} from "@/pages/investor/investor-research-pipeline";
import BankerOriginations from "@/pages/banker/banker-originations";
import AdminOriginations from "@/pages/admin/admin-originations";
import AdminOutreachApprovals from "@/pages/admin/admin-outreach-approvals";
import { InvestorPortalCuratedList, InvestorPortalCuratedDetail } from "@/pages/investor/investor-portal-curated";
import InvestorPortalLayout from "@/components/investor-portal-layout";
import IntakePipeline from "@/pages/intake/intake-pipeline";
import CallRequestAccept from "@/pages/investor/call-request-accept";
import FounderShowcase from "@/pages/founder-showcase";
import PublicShowcase from "@/pages/public-showcase";
import ImproveFloater from "@/components/improve-floater";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function Router() {
  const [location] = useLocation();

  if (location === "/" || location === "") {
    window.location.replace("/investor/login");
    return null;
  }

  // One-time password setup links — no auth required, must short-circuit
  // BEFORE the admin/banker/investor auth gates so the email link works
  // for users who have never logged in.
  if (location === "/investor/set-password") {
    return <SetPasswordPage audience="investor" />;
  }
  if (location === "/banker/set-password") {
    return <SetPasswordPage audience="banker" />;
  }
  if (location === "/admin/set-password") {
    return <SetPasswordPage audience="admin" />;
  }
  // ---------- LEGACY redirect: /1to10/* → /1to10/founder/* ----------
  // Old hardcoded URLs from before the cohort-aware refactor. Founders or
  // emails that still reference /1to10/login or /1to10/setup land here and
  // bounce forward to the new cohort-aware path. Skip if the URL already
  // contains /founder/ to avoid a loop.
  if (
    (location === "/1to10" || location.startsWith("/1to10/")) &&
    !location.startsWith("/1to10/founder")
  ) {
    const tail = location === "/1to10" ? "" : location.slice("/1to10".length);
    window.location.replace(`/1to10/founder${tail}`);
    return null;
  }

  // ---------- Cohort-aware Founder Portal: /<slug>/founder/* ----------
  // One route family, one component set, parameterized by cohort slug.
  // Theme + ritual gating come from cohort-themes.ts. Adding a new cohort
  // is an INSERT into cohorts plus a row in cohort-themes.ts. No new
  // routes, no duplicated pages.
  {
    const founderMatch = location.match(/^\/([a-z0-9][a-z0-9-]*)\/founder(?:\/(.*))?$/);
    const RESERVED_SLUGS = new Set(["admin", "investor", "banker", "c", "api"]);
    if (founderMatch && !RESERVED_SLUGS.has(founderMatch[1])) {
      const cohortSlug = founderMatch[1];
      const sub = founderMatch[2] || "";

      // Reject unknown cohort slugs at the route boundary. Without this,
      // /madeup/founder/login renders the generic fallback shell which
      // looks like a real cohort to a confused founder. The API still
      // rejects on cohort mismatch, but the UX would mislead. P6 invariant.
      if (!isKnownSlug(cohortSlug)) {
        return <UnknownCohortPage slug={cohortSlug} />;
      }

      // Public set-password landing — no auth gate, the token IS the auth.
      if (sub === "setup") {
        return <SetPasswordPage audience="founder" cohortSlug={cohortSlug} />;
      }
      // Public login.
      if (sub === "login") {
        return (
          <FounderCohortProvider slug={cohortSlug}>
            <FounderLogin1to10 />
          </FounderCohortProvider>
        );
      }
      // Force-change-password gate.
      if (sub === "change-password") {
        if (!readLocalStorage("founder_token")) {
          window.location.replace(`/${cohortSlug}/founder/login`);
          return null;
        }
        return (
          <FounderCohortProvider slug={cohortSlug}>
            <FounderChangePassword1to10 />
          </FounderCohortProvider>
        );
      }
      // Authed inner pages.
      if (!readLocalStorage("founder_token")) {
        window.location.replace(`/${cohortSlug}/founder/login`);
        return null;
      }
      if (readLocalStorage("founder_must_change_password") === "true") {
        window.location.replace(`/${cohortSlug}/founder/change-password`);
        return null;
      }
      const base = `/${cohortSlug}/founder`;
      // Hard-gate ritual routes by the cohort theme. Hiding a ritual from
      // the sidebar is not enough — without this, a founder in maydeal
      // (where Missions is off) can hit /maydeal/founder/missions directly
      // and see a ritual that was supposed to be sealed off. P6 invariant:
      // "no rituals from a sibling cohort leak in".
      const rituals = themeForSlug(cohortSlug).rituals;
      return (
        <FounderCohortProvider slug={cohortSlug}>
          <Founder1to10Layout>
            <Switch>
              <Route path={base}><FounderCommandCenter1to10 /></Route>
              {rituals.onboarding && <Route path={`${base}/onboarding`}><FounderStellarOnboarding /></Route>}
              {rituals.onboarding && <Route path={`${base}/onboarding/begin`}><FounderOnboardingBegin /></Route>}
              {rituals.thread && <Route path={`${base}/thread`}><FounderAdvisorThread /></Route>}
              {rituals.readiness && <Route path={`${base}/readiness`}><FounderReadiness /></Route>}
              {rituals.updates && <Route path={`${base}/updates`}><FounderWeeklyUpdate /></Route>}
              {rituals.proof && <Route path={`${base}/proof`}><FounderProof /></Route>}
              {rituals.review && <Route path={`${base}/review`}><FounderReviewRoom /></Route>}
              {rituals.missions && <Route path={`${base}/missions`}><FounderMissions /></Route>}
              {/* Mentor inbox supersedes the placeholder Network page (PRD §7.6).
                  We keep /network for one cohort cycle so old bookmarks redirect
                  forward, but the canonical route is /mentors. */}
              {rituals.network && <Route path={`${base}/mentors`}><FounderMentors /></Route>}
              {rituals.network && <Route path={`${base}/mentors/:id`}><FounderMentorDetail /></Route>}
              {rituals.network && <Route path={`${base}/network`}><FounderMentors /></Route>}
              {rituals.network && <Route path={`${base}/engagements/:id`}><FounderEngagementDetail /></Route>}
              {rituals.preview && <Route path={`${base}/preview`}><FounderInvestorPreview /></Route>}
              {rituals.ask && <Route path={`${base}/ask`}><FounderAsk /></Route>}
              <Route path={`${base}/settings`}><FounderSettings /></Route>
              <Route component={NotFound} />
            </Switch>
          </Founder1to10Layout>
        </FounderCohortProvider>
      );
    }
  }

  // Public magic-link landing for advisor-proposed calls (Phase 2). The token
  // in the URL is the auth — no investor session required.
  {
    const m = location.match(/^\/investor\/call-requests\/by-token\/([A-Za-z0-9]+)$/);
    if (m) {
      return <CallRequestAccept />;
    }
  }

  const isAdminRoute = location === "/admin" || location.startsWith("/admin/");
  const hasAdminToken = isAdminRoute && !!readLocalStorage("admin_token");

  if (isAdminRoute && !hasAdminToken && location !== "/admin/login") {
    window.location.replace("/admin/login");
    return null;
  }

  if (location === "/admin/login") {
    return <MasterAdminLogin />;
  }

  // Mentor portal — cross-cohort identity (PRD §8.3). No cohort prefix.
  // Public mentor application form (PRD §7.0.4) — no auth, anyone can submit.
  if (location === "/mentors/apply") return <MentorsApply />;
  // Public mentor profile (PRD §19.8) — gated by publicProfileOptIn server-side.
  {
    const m = location.match(/^\/m\/(\d+)$/);
    if (m) return <MentorPublicProfile />;
  }
  if (location === "/mentor/login") return <MentorLogin />;
  if (location === "/mentor/set-password") return <SetPasswordPage audience="mentor" />;
  if (location === "/mentor/activate") return <MentorActivate />;
  if (location === "/mentor" || location === "/mentor/") {
    return <MentorLayout><MentorHome /></MentorLayout>;
  }
  if (location === "/mentor/interests") {
    return <MentorLayout><MentorInterests /></MentorLayout>;
  }
  if (location === "/mentor/proposals") {
    return <MentorLayout><MentorProposals /></MentorLayout>;
  }
  if (location === "/mentor/engagements") {
    return <MentorLayout><MentorEngagements /></MentorLayout>;
  }
  {
    const m = location.match(/^\/mentor\/missions\/(\d+)$/);
    if (m) return <MentorLayout><MentorMissionDetail /></MentorLayout>;
  }
  {
    const m = location.match(/^\/mentor\/companies\/(\d+)$/);
    if (m) return <MentorLayout><MentorCompanyDetail /></MentorLayout>;
  }
  {
    const m = location.match(/^\/mentor\/engagements\/(\d+)$/);
    if (m) return <MentorLayout><MentorEngagementDetail /></MentorLayout>;
  }

  if (location === "/admin") {
    return (
      <AdminLayout>
        <AdminHome />
      </AdminLayout>
    );
  }

  if (location === "/admin/team") {
    return (
      <AdminLayout>
        <AdminTeam />
      </AdminLayout>
    );
  }

  if (location === "/admin/agents") {
    return (
      <AdminLayout>
        <AdminAgents />
      </AdminLayout>
    );
  }

  {
    const m = location.match(/^\/admin\/agents\/([a-z0-9_-]+)$/);
    if (m) {
      return (
        <AdminLayout>
          <AdminAgentDetail agent={m[1]} />
        </AdminLayout>
      );
    }
  }

  if (location === "/admin/bankers") {
    return (
      <AdminLayout>
        <AdminBankers />
      </AdminLayout>
    );
  }

  if (location === "/admin/mentors") {
    return (
      <AdminLayout>
        <AdminMentors />
      </AdminLayout>
    );
  }

  if (location === "/admin/advisor-health") {
    return (
      <AdminLayout>
        <AdminAdvisorHealth />
      </AdminLayout>
    );
  }

  if (location === "/admin/prds") {
    return (
      <AdminLayout>
        <AdminPrds />
      </AdminLayout>
    );
  }

  if (location === "/admin/spend") {
    return (
      <AdminLayout>
        <AdminSpend />
      </AdminLayout>
    );
  }

  if (location === "/admin/investors") {
    return (
      <AdminLayout>
        <AdminInvestors />
      </AdminLayout>
    );
  }

  if (location === "/admin/viren") {
    return (
      <AdminLayout>
        <AdminViren />
      </AdminLayout>
    );
  }

  if (location === "/admin/rateek") {
    return (
      <AdminLayout>
        <AdminRateek />
      </AdminLayout>
    );
  }

  if (location === "/admin/secondary") {
    return (
      <AdminLayout>
        <AdminSecondary />
      </AdminLayout>
    );
  }

  if (location === "/admin/health") {
    return (
      <AdminLayout>
        <AdminHealth />
      </AdminLayout>
    );
  }

  if (location === "/admin/timestamp-trail") {
    return (
      <AdminLayout>
        <AdminTimestampTrail />
      </AdminLayout>
    );
  }

  if (location === "/admin/onboarding-drips") {
    return (
      <AdminLayout>
        <AdminOnboardingDrips />
      </AdminLayout>
    );
  }

  if (location === "/admin/email") {
    return (
      <AdminLayout>
        <AdminEmail />
      </AdminLayout>
    );
  }

  if (location === "/admin/campaigns") {
    return (
      <AdminLayout>
        <AdminCampaigns />
      </AdminLayout>
    );
  }

  if (location === "/admin/research-pipeline") {
    return (
      <AdminLayout>
        <AdminResearchPipeline />
      </AdminLayout>
    );
  }

  {
    const researchMatch = location.match(/^\/admin\/research-pipeline\/(\d+)$/);
    if (researchMatch) {
      return (
        <AdminLayout>
          <AdminResearchPipelineDetail />
        </AdminLayout>
      );
    }
  }

  if (location === "/admin/bulletins") {
    return (
      <AdminLayout>
        <AdminBulletins />
      </AdminLayout>
    );
  }

  if (location === "/admin/conversations") {
    return (
      <AdminLayout>
        <AdminConversations />
      </AdminLayout>
    );
  }

  if (location === "/admin/campaigns/new") {
    return (
      <AdminLayout>
        <AdminCampaignNew />
      </AdminLayout>
    );
  }

  if (location === "/admin/campaigns/roadmap") {
    return (
      <AdminLayout>
        <AdminCampaignsRoadmap />
      </AdminLayout>
    );
  }

  if (location.startsWith("/admin/campaigns/")) {
    const idStr = location.replace("/admin/campaigns/", "").split("/")[0];
    const id = parseInt(idStr, 10);
    if (Number.isFinite(id)) {
      return (
        <AdminLayout>
          <AdminCampaignDetail campaignId={id} />
        </AdminLayout>
      );
    }
  }

  if (location === "/admin/originations") {
    return (
      <AdminLayout>
        <AdminOriginations />
      </AdminLayout>
    );
  }

  if (location === "/admin/founder-portal") {
    return (
      <AdminLayout>
        <AdminFounderPortal />
      </AdminLayout>
    );
  }

  if (location === "/admin/outreach") {
    return (
      <AdminLayout>
        <AdminOutreachApprovals />
      </AdminLayout>
    );
  }

  if (location === "/banker/login") {
    return <BankerLogin />;
  }

  // First-login force-change screen — gated by localStorage flag set at login
  // time. Lives outside the BankerLayout so the advisor can't reach the
  // dashboard until they've replaced the temp password.
  if (location === "/banker/change-password") {
    if (!readLocalStorage("banker_token")) {
      window.location.replace("/banker/login");
      return null;
    }
    return <BankerChangePassword />;
  }

  if (location.startsWith("/banker")) {
    const hasBankerToken = !!readLocalStorage("banker_token");
    if (!hasBankerToken) {
      window.location.replace("/banker/login");
      return null;
    }
    // Gate everything else behind the force-change flag.
    if (readLocalStorage("banker_must_change_password") === "true") {
      window.location.replace("/banker/change-password");
      return null;
    }
    return (
      <BankerLayout>
        <Switch>
          <Route path="/banker"><BankerDashboard /></Route>
          <Route path="/banker/tasks"><BankerTasks /></Route>
          <Route path="/banker/tasks/:id">{(params) => <BankerTaskDetail taskId={parseInt(params.id, 10)} />}</Route>
          <Route path="/banker/meera"><BankerMeera /></Route>
          <Route path="/banker/investors"><BankerInvestors /></Route>
          <Route path="/banker/investor-signals"><BankerInvestorSignals /></Route>
          <Route path="/banker/companies"><BankerCompanies /></Route>
          <Route path="/banker/my-founders"><BankerMyFounders /></Route>
          <Route path="/banker/artifacts"><BankerArtifacts /></Route>
          <Route path="/banker/mail"><BankerMail /></Route>
          <Route path="/banker/conversations"><BankerConversations /></Route>
          <Route path="/banker/inbox"><BankerInbox /></Route>
          <Route path="/banker/calendar"><BankerCalendar /></Route>
          <Route path="/banker/originations"><BankerOriginations /></Route>
          <Route component={NotFound} />
        </Switch>
      </BankerLayout>
    );
  }

  if (location.startsWith("/showcase/")) {
    const slugMatch = location.match(/^\/showcase\/(.+)/);
    const showcaseSlug = slugMatch ? slugMatch[1] : "";
    if (showcaseSlug) {
      return <PublicShowcase slug={showcaseSlug} />;
    }
  }

  if (location === "/investor/login/masteradmin") {
    window.location.replace("/admin/login");
    return null;
  }

  if (location === "/investor/login") {
    return <InvestorLogin />;
  }

  if (location === "/investor/change-password") {
    if (!readLocalStorage("investor_token")) {
      window.location.replace("/investor/login");
      return null;
    }
    return <InvestorChangePassword />;
  }

  if (location.startsWith("/investor/portal")) {
    if (readLocalStorage("investor_must_change_password") === "true") {
      window.location.replace("/investor/change-password");
      return null;
    }
    return (
      <InvestorPortalLayout>
        <Switch>
          <Route path="/investor/portal">
            <InvestorPortalDashboard />
          </Route>
          <Route path="/investor/portal/company/:companyId">
            {(params) => <InvestorPortalCompany companyId={parseInt(params.companyId, 10)} />}
          </Route>
          <Route path="/investor/portal/rofr">
            <InvestorPortalRofr />
          </Route>
          <Route path="/investor/portal/curated">
            <InvestorPortalCuratedList />
          </Route>
          <Route path="/investor/portal/curated/:slug">
            {(params) => <InvestorPortalCuratedDetail slug={params.slug} />}
          </Route>
          <Route path="/investor/portal/research/:pimId/read">
            {(params) => <InvestorResearchReadingRoom pimId={params.pimId} />}
          </Route>
          <Route path="/investor/portal/research/:pimId">
            {(params) => <InvestorResearchNarrative pimId={params.pimId} />}
          </Route>
          <Route path="/investor/portal/research">
            <InvestorResearchIndex />
          </Route>
          <Route path="/investor/portal/inbox">
            <InvestorPortalInbox />
          </Route>
          <Route path="/investor/portal/documents">
            <InvestorPortalDocuments />
          </Route>
          <Route path="/investor/portal/files-from-banker">
            <InvestorPortalFilesFromBanker />
          </Route>
          <Route path="/investor/portal/settings">
            <InvestorPortalSettings />
          </Route>
          <Route component={NotFound} />
        </Switch>
      </InvestorPortalLayout>
    );
  }

  if (location.startsWith("/c/")) {
    const slugMatch = location.match(/^\/c\/([^/]+)/);
    const cohortSlug = slugMatch ? slugMatch[1] : "";

    if (cohortSlug) {
      const subPath = location.replace(`/c/${cohortSlug}`, "") || "/";

      if (subPath.startsWith("/founder/")) {
        const match = subPath.match(/^\/founder\/(\d+)/);
        const founderId = match ? parseInt(match[1], 10) : 0;
        if (founderId > 0) {
          return (
            <FounderLayout founderId={founderId} cohortSlug={cohortSlug}>
              <Switch>
                <Route path="/c/:slug/founder/:id">
                  {(params) => <FounderHome founderId={parseInt(params.id, 10)} cohortSlug={params.slug} />}
                </Route>
                <Route path="/c/:slug/founder/:id/checkin">
                  {(params) => <FounderCheckin founderId={parseInt(params.id, 10)} />}
                </Route>
                <Route path="/c/:slug/founder/:id/approvals">
                  {(params) => <FounderApprovals founderId={parseInt(params.id, 10)} />}
                </Route>
                <Route path="/c/:slug/founder/:id/preview">
                  {(params) => <FounderPreview founderId={parseInt(params.id, 10)} />}
                </Route>
                <Route component={NotFound} />
              </Switch>
            </FounderLayout>
          );
        }
      }

      if (subPath.startsWith("/investor/")) {
        const match = subPath.match(/^\/investor\/(\d+)/);
        const investorId = match ? parseInt(match[1], 10) : 0;
        if (investorId > 0) {
          return (
            <InvestorLayout investorId={investorId} cohortSlug={cohortSlug}>
              <Switch>
                <Route path="/c/:slug/investor/:id">
                  {(params) => <InvestorHome investorId={parseInt(params.id, 10)} cohortSlug={params.slug} />}
                </Route>
                <Route path="/c/:slug/investor/:id/company/:companyId">
                  {(params) => <InvestorMicrosite investorId={parseInt(params.id, 10)} companyId={parseInt(params.companyId, 10)} />}
                </Route>
                <Route component={NotFound} />
              </Switch>
            </InvestorLayout>
          );
        }
      }

      return (
        <Layout cohortSlug={cohortSlug}>
          <Switch>
            <Route path="/c/:slug">
              {(params) => <CommandCenter cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/intake">
              {(params) => <IntakePipeline cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/companies">
              {(params) => <Companies cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/companies/:id/showcase">
              {(params) => <FounderShowcase companyId={parseInt(params.id, 10)} cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/companies/:id">
              {(params) => <CompanyDetail id={parseInt(params.id, 10)} cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/founders">
              {(params) => <Founders cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/investors">
              {(params) => <Investors cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/review-queue">
              {(params) => <ReviewQueue cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/tasks">
              {(params) => <TasksPage cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/updates">
              {(params) => <Updates cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/documents">
              {(params) => <Documents cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/media">
              {(params) => <MediaPage cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/audit">
              {(params) => <Audit cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/members">
              {(params) => <CohortMembers cohortSlug={params.slug} />}
            </Route>
            <Route path="/c/:slug/announcements">
              {(params) => <Announcements cohortSlug={params.slug} />}
            </Route>
            <Route component={NotFound} />
          </Switch>
        </Layout>
      );
    }
  }

  return (
    <AdminLayout>
      <NotFound />
    </AdminLayout>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <Router />
          <ImproveFloater />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
